import React, { useEffect, useRef, useState } from 'react';
import { LockClosedIcon, LockOpenIcon } from '@heroicons/react/24/outline';

/**
 * AdminPinModal Component
 * PIN verification for admin operations
 */
export const AdminPinModal = ({
  isOpen,
  onClose,
  onVerify,
  loading,
  error,
  title = 'Admin Authentication',
  description = 'Enter admin PIN to continue'
}) => {
  const [pin, setPin] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [localError, setLocalError] = useState('');
  const pinInputRef = useRef(null);
  const MAX_ATTEMPTS = 3;
  const isLocked = attempts >= MAX_ATTEMPTS;

  useEffect(() => {
    if (!isOpen) return;

    setLocalError('');

    const timer = setTimeout(() => {
      pinInputRef.current?.focus();
    }, 50);

    return () => clearTimeout(timer);
  }, [isOpen]);

  const handleSubmit = async () => {
    const pinString = String(pin).trim();
    if (pinString.length !== 4 || !/^\d{4}$/.test(pinString)) {
      setLocalError('PIN must be exactly 4 digits');
      return;
    }

    try {
      setLocalError('');
      await onVerify(pinString);
      setPin('');
      setAttempts(0);
    } catch (err) {
      setAttempts(prev => prev + 1);
      setPin('');
      if (attempts + 1 >= MAX_ATTEMPTS) {
        setTimeout(() => {
          onClose();
          setAttempts(0);
          setPin('');
        }, 2000);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-8 w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-6">
          {isLocked ? (
            <LockClosedIcon className="h-12 w-12 text-red-600 mx-auto mb-3" />
          ) : (
            <LockOpenIcon className="h-12 w-12 text-blue-600 mx-auto mb-3" />
          )}
          <h3 className="text-2xl font-bold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        </div>

        {/* Locked Message */}
        {isLocked && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6 text-center">
            <p className="text-red-700 font-semibold text-sm">
              Too many incorrect attempts
            </p>
            <p className="text-red-600 text-xs mt-1">
              Please try again in a moment
            </p>
          </div>
        )}

        {/* Error Message */}
        {(localError || error) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-center">
            <p className="text-red-700 text-sm font-medium">{localError || error}</p>
          </div>
        )}

        {/* Attempt Counter */}
        {attempts > 0 && !isLocked && (
          <div className="mb-4 text-center">
            <p className="text-sm text-gray-600">
              Attempt {attempts} of {MAX_ATTEMPTS}
            </p>
            <div className="flex gap-1 justify-center mt-2">
              {[...Array(MAX_ATTEMPTS)].map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full ${
                    i < attempts ? 'bg-red-500' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* PIN Input */}
        {!isLocked && (
          <div className="mb-6">
            <input
              ref={pinInputRef}
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              value={pin}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                setPin(value);
                setLocalError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit();
                }
              }}
              placeholder="• • • •"
              disabled={loading}
              maxLength="4"
              className="w-full text-center text-4xl font-bold tracking-widest border-2 border-gray-300 rounded-lg py-4 focus:outline-none focus:border-blue-500 disabled:bg-gray-100"
              autoFocus
            />
            <p className="text-center text-xs text-gray-500 mt-2">
              Enter 4-digit PIN
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || pin.length !== 4 || isLocked}
            className={`flex-1 px-4 py-2 rounded-lg font-semibold text-white transition ${
              loading || pin.length !== 4 || isLocked
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminPinModal;
